import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes, elevation } from "@similarweb/styles";

import type { Playbook } from "../../types/playbooks.types";

import { getPlaybooksColumns, type PlaybooksColumnHandlers } from "./columns";
import { SELECTION_COLUMN_WIDTH, StyledTableCardWrapper } from "./styles";

const PAGE_SIZE = 20;

type PlaybooksTableProps = {
    rows: Playbook[];
    handlers: PlaybooksColumnHandlers;
    dataAutomation: string;
    title: string;
    subtitle?: string;
    selectable?: boolean;
    onDeleteSelected?: (ids: number[]) => Promise<void> | void;
};

export const PlaybooksTable = ({
    rows,
    handlers,
    dataAutomation,
    title,
    subtitle,
    selectable = false,
    onDeleteSelected,
}: PlaybooksTableProps) => {
    const { translate } = useTranslate();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);

    const columnConfig = useMemo(() => getPlaybooksColumns(translate, handlers), [translate, handlers]);

    const handleDeleteSelected = async () => {
        setIsDeleting(true);
        try {
            await onDeleteSelected?.(selectedIds);
            setSelectedIds([]);
        } catch {
            // Keep the selection on failure; the container surfaces the error toast.
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <StyledTableCardWrapper
            borderRadius="6px"
            background={colorsPalettes.carbon["0"]}
            boxShadow={elevation.Z1}
            overflow="hidden"
            data-automation={dataAutomation}
        >
            <Table<Playbook>
                data={rows}
                rowKey="playbookId"
                columnConfig={columnConfig}
                pagination={{ pageSize: PAGE_SIZE }}
                withArrowButtons={false}
                selection={
                    selectable
                        ? {
                              enabled: true,
                              columnWidth: SELECTION_COLUMN_WIDTH,
                              fixed: true,
                              selectedRowKeys: selectedIds,
                              onChange: (keys) => setSelectedIds(keys.map((key) => Number(key))),
                          }
                        : undefined
                }
            >
                <Table.TableTop borderBottom>
                    <Table.TableTop.Title
                        title={title}
                        subtitle={subtitle}
                    />
                    <Table.TableTop.Selection
                        enabled={selectable}
                        selectText={translate("si.playbooks.selected_count")}
                        actionButtons={[
                            {
                                iconName: "delete",
                                children: translate("si.common.button.delete"),
                                onClick: () => void handleDeleteSelected(),
                                isLoading: isDeleting,
                                dataAutomation: "playbooks-delete-selected",
                            },
                        ]}
                    />
                </Table.TableTop>
            </Table>
        </StyledTableCardWrapper>
    );
};
